import React from "react";
import Section from "./Section";
import img1 from "../../img/10114186_43827471.jpg";

export default function HTML_Attributes() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🔖 HTML Attributes</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        HTML <strong>attributes</strong> provide additional information about
        elements. They are written inside the opening tag and usually come as
        <code> name="value"</code> pairs. Attributes help control the behavior
        and appearance of elements.
      </p>

      {/* Section 1️⃣ — Basic Attribute Example */}
      <Section
        title="1️⃣ Basic Attribute Example"
        html={`<p title="This is a tooltip">Hover over this text!</p>`}
        description="Attributes are added to HTML elements to define extra information. The title attribute displays a tooltip when you hover over the text."
      />

      {/* Section 2️⃣ — The href Attribute */}
      <Section
        title="2️⃣ The href Attribute"
        html={`<a href="https://www.google.com" target="_blank">Visit Google</a>`}
        description="The href attribute in the <a> tag defines the link destination, and target='_blank' opens the link in a new tab."
      />

      {/* Section 3️⃣ — The src Attribute */}
      <Section
        title="3️⃣ The src Attribute"
        html={`<img src="${img1}" alt="Sample Image" width="150" height="150" />`}
        description="The src attribute specifies the image path or URL. The alt attribute describes the image for screen readers or when the image can’t load."
      />

      {/* Section 4️⃣ — The style Attribute */}
      <Section
        title="4️⃣ The style Attribute"
        html={`<p style="color: blue; font-size: 20px;">Styled text using inline CSS!</p>`}
        description="The style attribute allows you to apply CSS styles directly to an element."
      />

      {/* Section 5️⃣ — The lang Attribute */}
      <Section
        title="5️⃣ The lang Attribute"
        html={`<p lang="fr">Bonjour le monde!</p>`}
        description="The lang attribute declares the language of the element’s content — important for accessibility and SEO."
      />

      {/* Section 6️⃣ — The title Attribute */}
      <Section
        title="6️⃣ The title Attribute"
        html={`<abbr title="HyperText Markup Language">HTML</abbr>`}
        description="The title attribute adds extra information (tooltip) visible when the user hovers over an element."
      />

      {/* Section 7️⃣ — The alt Attribute */}
      <Section
        title="7️⃣ The alt Attribute"
        html={`<img src="missing.jpg" alt="This text appears if image fails to load" width="150" />`}
        description="The alt attribute provides alternative text for images — it’s crucial for accessibility and SEO."
      />

      {/* Section 8️⃣ — The id & class Attributes */}
      <Section
        title="8️⃣ The id and class Attributes"
        html={`<div id="uniqueBox" class="blue-box">I have an ID and a class!</div>`}
        description="The id uniquely identifies an element, while class groups elements for styling or scripting."
      />

      {/* Section 9️⃣ — The data-* Attributes */}
      <Section
        title="9️⃣ The data-* Attributes"
        html={`<button data-user="Sanjana" onclick="alert(this.dataset.user)">Click me</button>`}
        description="Custom data attributes (data-*) store extra information directly in HTML elements and can be accessed in JavaScript using dataset."
      />

      {/* Section 🔟 — Multiple Attributes Together */}
      <Section
        title="🔟 Multiple Attributes Example"
        html={`<a href="https://openai.com" title="Visit OpenAI" target="_blank" style="color:green; text-decoration:none;">OpenAI Website</a>`}
        description="You can use multiple attributes together to define link destination, style, and tooltip behavior all at once."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Attributes are written inside the opening tag of elements.</li>
          <li>
            They come in name/value pairs — e.g., <code>name="value"</code>.
          </li>
          <li>
            Common attributes include <b>href</b>, <b>src</b>, <b>alt</b>,{" "}
            <b>style</b>, and <b>title</b>.
          </li>
          <li>
            Use <b>id</b> and <b>class</b> for identification and styling.
          </li>
          <li>
            Custom <b>data-*</b> attributes allow storing extra info.
          </li>
        </ul>
      </div>
    </div>
  );
}
